/*
================================================================================
FILE HEADER — apps/studio-web/src/components/CustomCommandsPanel.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Custom Commands (Option C)| Created              |
| 2  | 2026-06-25 | Claude Sonnet 4.6 | FR1 — UX polish          | Animated builder,    |
|    |            |                   |                          | OR divider, multi-   |
|    |            |                   |                          | locator manual entry |
| 3  | 2026-06-25 | Claude Sonnet 4.6 | Bug fix — accumulating   | capturedCandidates   |
| 4  | 2026-11-05 | Claude Opus 5.5   | Public hosting + rename | Visible name Plex → Scriptwave; hosted at scriptwave.qagreen.com |
|    |            |                   | manual candidates +      | separated from       |
|    |            |                   | spurious animations      | manualRows; capture  |
|    |            |                   |                          | callback passes      |
|    |            |                   |                          | Selector directly    |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/components/RecorderPanel.tsx

IMPORTANT POINTS
----------------
1. SelectorField NEVER re-derives capturedCandidates from the value prop.
   capturedCandidates is LOCAL state, only updated via the direct capture callback
   (mirror click). This prevents manual typing from appearing in "Element Captured"
   and prevents the justCaptured animation from firing on every keystroke.

2. onRequestCapture now accepts a second argument: the SelectorField's own callback.
   The flow: SelectorField → FieldRenderer → CommandBuilder → CustomCommandsPanel
   → App.tsx → ELEMENT_CAPTURED arrives → captureCallback(sel) fires directly into
   SelectorField, bypassing the value prop round-trip.

3. fieldValues[field.id] is still updated (for step building) but SelectorField
   never reads it back to update capturedCandidates. The value prop is write-only
   from SelectorField's perspective after mount.
================================================================================
*/

import { useState, useEffect, useRef, useCallback } from 'react';
import type { IRStep, Selector, Candidate, CandidateStrategy } from '@plex/ir';
import { COMMAND_TABS, getCommandsForTab, type WebCommand, type CommandField } from '../lib/webCommands.js';

// ─────────────────────────────────────────────────────────────────────────────
// Animation styles — injected once into <head>
// ─────────────────────────────────────────────────────────────────────────────

function useAnimationStyles() {
  useEffect(() => {
    if (document.getElementById('plex-cmd-anim')) return;
    const s = document.createElement('style');
    s.id = 'plex-cmd-anim';
    s.textContent = `
      @keyframes plexBuilderEntrance {
        0%   { border-color:#7c3aed; box-shadow:0 0 0 4px rgba(124,58,237,.45); }
        65%  { border-color:#7c3aed; box-shadow:0 0 0 4px rgba(124,58,237,.45); }
        100% { border-color:#27272a; box-shadow:none; }
      }
      @keyframes plexPulseGreen {
        0%,100% { box-shadow:0 0 0 0px rgba(34,197,94,0); }
        50%     { box-shadow:0 0 0 6px rgba(34,197,94,.4); }
      }
      @keyframes plexPulsePurple {
        0%,100% { box-shadow:0 0 0 0px rgba(124,58,237,0); }
        50%     { box-shadow:0 0 0 5px rgba(124,58,237,.45); }
      }
      @keyframes plexCaptureFlash {
        0%   { background:rgba(34,197,94,.25); transform:scale(1.01); }
        100% { background:rgba(34,197,94,.07); transform:scale(1); }
      }
      @keyframes plexFadeSlideIn {
        from { opacity:0; transform:translateY(-4px); }
        to   { opacity:1; transform:translateY(0); }
      }
    `;
    document.head.appendChild(s);
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Strategy options
// ─────────────────────────────────────────────────────────────────────────────

const LOCATOR_STRATEGIES: Array<{ value: CandidateStrategy; label: string; placeholder: string }> = [
  { value: 'a11yId',       label: 'ID / TestID',       placeholder: 'data-testid value or id'      },
  { value: 'text',         label: 'Text (exact)',       placeholder: 'exact visible text'            },
  { value: 'textContains', label: 'Text (contains)',    placeholder: 'partial text'                  },
  { value: 'contentDesc',  label: 'ARIA Label',         placeholder: 'aria-label value'              },
  { value: 'className',    label: 'Role + Name',        placeholder: 'button[Sign In]'               },
  { value: 'css',          label: 'CSS Selector',       placeholder: '.btn-primary, #submit-btn'     },
  { value: 'xpath',        label: 'XPath',              placeholder: '//button[@type="submit"]'      },
];

const STRATEGY_COLOR: Record<string, string> = {
  a11yId:'#22c55e', text:'#22c55e', contentDesc:'#22c55e', className:'#22c55e',
  textContains:'#eab308', relative:'#eab308',
  css:'#ef4444', xpath:'#ef4444',
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ManualRow { strategy: CandidateStrategy; value: string; }

type CaptureRequestFn = (captureCallback: (sel: Selector | null) => void) => void;

type Props = {
  onAddSteps:             (steps: IRStep[]) => void;
  onRequestCapture:       (callback: (selector: Selector | null) => void) => void;
  captureActive:          boolean;
  /** Called when CommandBuilder opens (true) or closes (false).
   *  RecorderPanel uses this to give the builder full panel height. */
  onBuilderActiveChange?: (active: boolean) => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────

export function CustomCommandsPanel({ onAddSteps, onRequestCapture, captureActive, onBuilderActiveChange }: Props) {
  useAnimationStyles();

  const [activeTab,      setActiveTab]      = useState('Navigate');
  const [activeCommand,  setActiveCommand]  = useState<WebCommand | null>(null);
  const [fieldValues,    setFieldValues]    = useState<Record<string, string>>({});
  const [captureFieldId, setCaptureFieldId] = useState<string | null>(null);
  const [addedFlash,     setAddedFlash]     = useState(false);

  // Map fieldId → direct capture callback registered by SelectorField
  const captureCallbacksRef = useRef<Map<string, (sel: Selector | null) => void>>(new Map());

  const tabCommands  = getCommandsForTab(activeTab);
  const activeTabDef = COMMAND_TABS.find(t => t.id === activeTab);

  const openCommand = (cmd: WebCommand) => {
    setActiveCommand(cmd);
    captureCallbacksRef.current.clear();
    const defaults: Record<string, string> = {};
    for (const f of cmd.fields) {
      if (f.defaultValue !== undefined) defaults[f.id] = String(f.defaultValue);
    }
    setFieldValues(defaults);
    setCaptureFieldId(null);
    onBuilderActiveChange?.(true);  // signal RecorderPanel: builder opened
  };

  const closeBuilder = () => {
    setActiveCommand(null);
    setFieldValues({});
    setCaptureFieldId(null);
    captureCallbacksRef.current.clear();
    onBuilderActiveChange?.(false); // signal RecorderPanel: builder closed
  };

  const addSteps = () => {
    if (!activeCommand) return;
    for (const f of activeCommand.fields) {
      if (f.required && !fieldValues[f.id]?.trim()) {
        alert(`"${f.label}" is required`);
        return;
      }
    }
    try {
      const steps = activeCommand.buildSteps(fieldValues);
      onAddSteps(steps);
      setAddedFlash(true);
      setTimeout(() => setAddedFlash(false), 800);
      closeBuilder();
    } catch (err) {
      alert('Failed to build step: ' + String(err));
    }
  };

  // requestCapture: called by FieldRenderer with the field ID + SelectorField's own callback.
  // We set capture mode, wait for App.tsx to resolve, then notify BOTH fieldValues AND
  // the SelectorField's local state (via captureCallback) — so SelectorField never needs
  // to re-derive capturedCandidates from the value prop.
  const requestCapture = useCallback((fieldId: string, captureCallback: (sel: Selector | null) => void) => {
    captureCallbacksRef.current.set(fieldId, captureCallback);
    setCaptureFieldId(fieldId);
    onRequestCapture((sel) => {
      // Update fieldValues so the step builder sees the captured value
      if (sel) {
        setFieldValues(prev => ({ ...prev, [fieldId]: JSON.stringify(sel) }));
      }
      setCaptureFieldId(null);
      // Notify SelectorField directly — this is the key: local state update, not via value prop
      captureCallbacksRef.current.get(fieldId)?.(sel);
    });
  }, [onRequestCapture]);

  return (
    <div style={activeCommand ? { display:'flex', flexDirection:'column', height:'100%' } : undefined}>
      {/* ── Tab row ── */}
      <div style={{ padding:'6px 14px 0', borderBottom:'1px solid #27272a', flexShrink:0 }}>
        <div style={{ fontSize:10, fontWeight:700, color:'#52525b', letterSpacing:'0.08em', marginBottom:6 }}>
          CUSTOM COMMANDS
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:4, paddingBottom:8 }}>
          {COMMAND_TABS.map(tab => (
            <button key={tab.id}
              onClick={() => { setActiveTab(tab.id); closeBuilder(); }}
              title={tab.active ? tab.label : `${tab.label} — Phase 4`}
              style={{
                background: activeTab === tab.id ? '#27272a' : 'none',
                border: `1px solid ${activeTab === tab.id ? '#52525b' : '#3f3f46'}`,
                borderRadius:5, padding:'3px 8px',
                color: activeTab === tab.id ? '#e4e4e7' : tab.active ? '#71717a' : '#3f3f46',
                cursor: tab.active ? 'pointer' : 'not-allowed',
                fontSize:11, opacity: tab.active ? 1 : 0.5,
              }}
            >{tab.label}</button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      {!activeTabDef?.active ? (
        <div style={{ padding:'10px 14px', fontSize:12, color:'#3f3f46', textAlign:'center' }}>
          {activeTab} commands — coming in Phase 4
        </div>
      ) : activeCommand ? (
        <CommandBuilder
          key={activeCommand.id}
          command={activeCommand}
          fieldValues={fieldValues}
          captureFieldId={captureFieldId}
          captureActive={captureActive}
          addedFlash={addedFlash}
          onChange={(id, val) => setFieldValues(prev => ({ ...prev, [id]: val }))}
          onRequestCapture={requestCapture}
          onAdd={addSteps}
          onClose={closeBuilder}
          fullPanel   // always full-panel now that RecorderPanel manages the height
        />
      ) : (
        <div style={{ padding:'10px 14px', display:'flex', flexWrap:'wrap', gap:6 }}>
          {tabCommands.length === 0
            ? <div style={{ fontSize:12, color:'#3f3f46' }}>No commands in this tab yet.</div>
            : tabCommands.map(cmd => (
                <CommandCard key={cmd.id} cmd={cmd} onClick={() => openCommand(cmd)} />
              ))
          }
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CommandCard
// ─────────────────────────────────────────────────────────────────────────────

function CommandCard({ cmd, onClick }: { cmd: WebCommand; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      title={cmd.description}
      style={{
        background: hover ? 'rgba(124,58,237,.1)' : '#18181b',
        border: `1px solid ${hover ? '#7c3aed' : '#27272a'}`,
        borderRadius:7, padding:'7px 10px', cursor:'pointer',
        textAlign:'left', minWidth:100, transition:'all 0.1s',
      }}
    >
      <div style={{ fontSize:14, marginBottom:2 }}>{cmd.icon}</div>
      <div style={{ fontSize:11, fontWeight:600, color: hover ? '#c4b5fd' : '#e4e4e7' }}>{cmd.name}</div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CommandBuilder
// ─────────────────────────────────────────────────────────────────────────────

function CommandBuilder({ command, fieldValues, captureFieldId, captureActive, addedFlash,
  onChange, onRequestCapture, onAdd, onClose, fullPanel }: {
  command:          WebCommand;
  fieldValues:      Record<string, string>;
  captureFieldId:   string | null;
  captureActive:    boolean;
  addedFlash:       boolean;
  fullPanel?:       boolean;
  onChange:         (id: string, val: string) => void;
  onRequestCapture: (fieldId: string, cb: (sel: Selector | null) => void) => void;
  onAdd:            () => void;
  onClose:          () => void;
}) {
  // Three-section layout — fills the available panel height:
  //   ① Header  — sticky, always visible (command icon + name + ✕)
  //   ② Fields  — flex:1, overflowY:auto — scrolls when content is taller than available space
  //   ③ Actions — sticky, always visible ("+ Add Step" / "Cancel")
  //
  // RecorderPanel gives this component `flex:1` so the builder fills the right
  // panel completely, replacing the step list while a command is being configured.
  return (
    <div style={{
      display:'flex', flexDirection:'column',
      flex: fullPanel ? 1 : undefined,
      minHeight: 0, // required for flex children to shrink correctly
      background:'#111113',
      borderTop:'1px solid #1c1c1f',
      border:'1.5px solid #27272a',
      animation:'plexBuilderEntrance 2.2s ease-out forwards',
      overflow:'hidden',
    }}>

      {/* ① Header — always visible */}
      <div style={{ padding:'14px 16px 12px', flexShrink:0, borderBottom:'1px solid #27272a' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:20 }}>{command.icon}</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#e4e4e7' }}>{command.name}</div>
            <div style={{ fontSize:11, color:'#71717a', marginTop:2 }}>{command.description}</div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', color:'#52525b', cursor:'pointer', fontSize:20, lineHeight:1, padding:'2px 6px' }}
            onMouseEnter={e => { (e.currentTarget).style.color='#ef4444'; }}
            onMouseLeave={e => { (e.currentTarget).style.color='#52525b'; }}
            title="Close builder"
          >✕</button>
        </div>
      </div>

      {/* ② Fields — takes all remaining height, scrolls naturally */}
      <div style={{
        flex:1, overflowY:'auto', minHeight:0,
        padding:'14px 16px',
      }}>
        {command.fields.length === 0 ? (
          <div style={{ fontSize:13, color:'#71717a', padding:'16px', textAlign:'center',
            background:'#18181b', borderRadius:8, border:'1px solid #27272a' }}>
            ✓ No configuration needed — click <strong style={{ color:'#c4b5fd' }}>+ Add Step</strong> below
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {command.fields.map(field => (
              <FieldRenderer
                key={field.id}
                field={field}
                value={fieldValues[field.id] ?? ''}
                isCaptureActive={captureFieldId === field.id && captureActive}
                onChange={val => onChange(field.id, val)}
                onRequestCapture={cb => onRequestCapture(field.id, cb)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ③ Actions — always visible at the bottom */}
      <div style={{
        padding:'12px 16px 14px', flexShrink:0,
        borderTop:'1px solid #27272a', background:'#111113',
      }}>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onAdd}
            style={{
              background: addedFlash ? '#22c55e' : '#7c3aed',
              border:'none', borderRadius:8, padding:'11px 16px',
              color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700,
              transition:'background 0.2s', flex:1,
              boxShadow: addedFlash ? 'none' : '0 2px 8px rgba(124,58,237,0.35)',
            }}
            onMouseEnter={e => { if (!addedFlash) (e.currentTarget).style.background='#6d28d9'; }}
            onMouseLeave={e => { if (!addedFlash) (e.currentTarget).style.background='#7c3aed'; }}
          >{addedFlash ? '✓ Step Added!' : '+ Add Step'}</button>
          <button onClick={onClose}
            style={{ background:'#27272a', border:'none', borderRadius:8, padding:'11px 16px',
              color:'#a1a1aa', cursor:'pointer', fontSize:13, fontWeight:500 }}
            onMouseEnter={e => { (e.currentTarget).style.background='#3f3f46'; }}
            onMouseLeave={e => { (e.currentTarget).style.background='#27272a'; }}
          >← Back</button>
        </div>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldRenderer
// ─────────────────────────────────────────────────────────────────────────────

function FieldRenderer({ field, value, isCaptureActive, onChange, onRequestCapture }: {
  field:            CommandField;
  value:            string;
  isCaptureActive:  boolean;
  onChange:         (val: string) => void;
  onRequestCapture: CaptureRequestFn;
}) {
  const inputStyle: React.CSSProperties = {
    background:'#09090b', border:'1px solid #3f3f46', borderRadius:6,
    padding:'6px 10px', color:'#e4e4e7', fontSize:12, outline:'none', width:'100%',
    boxSizing:'border-box',
  };

  if (field.type === 'selector') {
    return <SelectorField field={field} value={value} isCaptureActive={isCaptureActive}
              onChange={onChange} onRequestCapture={onRequestCapture} />;
  }

  return (
    <div>
      <FieldLabel field={field} />
      {field.type === 'select' ? (
        <select value={value} onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, cursor:'pointer' }}>
          {(field.options ?? []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : field.type === 'code' ? (
        <textarea value={value} onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder} rows={4}
          style={{ ...inputStyle, fontFamily:'monospace', fontSize:11, resize:'vertical', lineHeight:1.6 }} />
      ) : field.type === 'number' ? (
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <input type="number" value={value} onChange={e => onChange(e.target.value)}
            placeholder={field.placeholder} style={{ ...inputStyle, width:120 }} />
          {field.hint && <span style={{ fontSize:11, color:'#52525b' }}>{field.hint}</span>}
        </div>
      ) : (
        <input type="text" value={value} onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder} style={inputStyle} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SelectorField — dual-path element selector with separated state
// ─────────────────────────────────────────────────────────────────────────────

function SelectorField({ field, value, isCaptureActive, onChange, onRequestCapture }: {
  field:            CommandField;
  value:            string;
  isCaptureActive:  boolean;
  onChange:         (val: string) => void;
  onRequestCapture: CaptureRequestFn;
}) {
  // ── CRITICAL: capturedCandidates is LOCAL state, NEVER re-derived from value prop.
  // It is ONLY updated by the direct capture callback when user taps on mirror.
  // This prevents typing in manual rows from polluting the "Element Captured" section.
  const [capturedCandidates, setCapturedCandidates] = useState<Candidate[]>(() => {
    // One-time initialisation from value (handles pre-populated selectors)
    try {
      const sel = JSON.parse(value) as Selector;
      return sel.candidates.filter(c => c.addedBy === 'recorder');
    } catch { return []; }
  });
  const [capturedDesc, setCapturedDesc] = useState<string>(() => {
    try { return (JSON.parse(value) as Selector).description; } catch { return ''; }
  });

  // Manual rows are also LOCAL — never included in capturedCandidates
  const [manualRows, setManualRows] = useState<ManualRow[]>(() => {
    try {
      const sel = JSON.parse(value) as Selector;
      const userCandidates = sel.candidates.filter(c => c.addedBy === 'user');
      return userCandidates.length > 0
        ? userCandidates.map(c => ({ strategy: c.strategy, value: c.value }))
        : [{ strategy: 'css' as CandidateStrategy, value: '' }];
    } catch { return [{ strategy: 'css' as CandidateStrategy, value: '' }]; }
  });

  const [showManual,    setShowManual]    = useState(false);
  const [justCaptured,  setJustCaptured]  = useState(false);
  const [manualPulsing, setManualPulsing] = useState(false);

  const hasCaptured = capturedCandidates.length > 0;

  // Build and emit the combined Selector value whenever either source changes.
  // This is the ONLY place onChange is called — guarantees no feedback loops.
  const emitCombined = useCallback((captured: Candidate[], manual: ManualRow[], desc: string) => {
    const manualCandidates: Candidate[] = manual
      .filter(r => r.value.trim())
      .map(r => ({ strategy: r.strategy, value: r.value.trim(), addedBy: 'user' as const }));
    const all = [...captured, ...manualCandidates];
    if (all.length === 0) { onChange(''); return; }
    const description = desc || manualCandidates[0]?.value || 'element';
    onChange(JSON.stringify({ description, candidates: all } satisfies Selector));
  }, [onChange]);

  // Handle mirror capture — ONLY path that updates capturedCandidates
  const handleTapMirror = () => {
    onRequestCapture((sel) => {
      if (!sel) return;
      setCapturedCandidates(sel.candidates);
      setCapturedDesc(sel.description);
      setJustCaptured(true);
      setTimeout(() => setJustCaptured(false), 2500);
      // Emit combined (captured + any existing manual)
      emitCombined(sel.candidates, manualRows, sel.description);
    });
  };

  // Handle manual row changes — never touches capturedCandidates
  const handleManualChange = (index: number, patch: Partial<ManualRow>) => {
    const next = manualRows.map((r, i) => i === index ? { ...r, ...patch } : r);
    setManualRows(next);
    emitCombined(capturedCandidates, next, capturedDesc); // capturedCandidates unchanged
  };

  const addManualRow = () => {
    setManualRows(prev => [...prev, { strategy: 'css', value: '' }]);
    // don't call emitCombined — new empty row doesn't change the Selector
  };

  const removeManualRow = (index: number) => {
    const next = manualRows.filter((_, i) => i !== index);
    const safe = next.length > 0 ? next : [{ strategy: 'css' as CandidateStrategy, value: '' }];
    setManualRows(safe);
    emitCombined(capturedCandidates, safe, capturedDesc);
  };

  const clearCapture = () => {
    setCapturedCandidates([]);
    setCapturedDesc('');
    emitCombined([], manualRows, '');
  };

  const handleOpenManual = () => {
    setShowManual(prev => {
      if (!prev) { setManualPulsing(true); setTimeout(() => setManualPulsing(false), 1800); }
      return !prev;
    });
  };

  const placeholderFor = (s: CandidateStrategy) =>
    LOCATOR_STRATEGIES.find(x => x.value === s)?.placeholder ?? 'value';

  return (
    <div>
      <FieldLabel field={field} />

      {/* ── Path 1: Tap on Mirror ── */}
      <button onClick={handleTapMirror}
        style={{
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          width:'100%', padding:'10px 12px', borderRadius:8, cursor:'pointer',
          fontSize:13, fontWeight:700,
          background: isCaptureActive ? 'rgba(34,197,94,.18)' : '#052e16',
          border: `2px solid ${isCaptureActive ? '#22c55e' : '#166534'}`,
          color: isCaptureActive ? '#4ade80' : '#86efac',
          animation: isCaptureActive ? 'plexPulseGreen 1s ease-in-out infinite' : 'none',
          transition:'all 0.2s',
        }}
      >
        <span style={{ fontSize:18 }}>🎯</span>
        {isCaptureActive ? 'Click any element on the mirror…' : 'Tap on Mirror to Select Element'}
      </button>

      {/* ── Element Captured display ── */}
      {hasCaptured && (
        <div style={{
          marginTop:10, borderRadius:8, border:'2px solid #22c55e',
          background:'rgba(34,197,94,.07)', padding:'10px 12px',
          animation: justCaptured
            ? 'plexCaptureFlash 0.4s ease-out, plexPulseGreen 1.2s ease-in-out 0.4s 2'
            : 'none',
        }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
            <span style={{ fontSize:14, fontWeight:800, color:'#4ade80' }}>
              ✓ Element Captured
            </span>
            <button onClick={clearCapture}
              style={{ background:'none', border:'1px solid #166534', borderRadius:4,
                padding:'2px 8px', color:'#52525b', cursor:'pointer', fontSize:10 }}
              onMouseEnter={e => { (e.currentTarget).style.color='#ef4444'; }}
              onMouseLeave={e => { (e.currentTarget).style.color='#52525b'; }}
            >Clear</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
            {capturedCandidates.map((c, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{
                  fontSize:9, fontWeight:700, color: STRATEGY_COLOR[c.strategy] ?? '#71717a',
                  border:`1px solid ${STRATEGY_COLOR[c.strategy] ?? '#3f3f46'}`,
                  borderRadius:3, padding:'2px 6px', fontFamily:'monospace', flexShrink:0,
                }}>{c.strategy}</span>
                <span style={{
                  fontSize:12, fontWeight: i===0 ? 600 : 400,
                  color: i===0 ? '#e4e4e7' : '#a1a1aa',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                }}>{c.value}</span>
                {i === 0 && (
                  <span style={{ fontSize:9, color:'#22c55e', flexShrink:0,
                    background:'rgba(34,197,94,.15)', borderRadius:3, padding:'1px 5px' }}>
                    PRIMARY
                  </span>
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop:6, fontSize:10, color:'#52525b' }}>
            {capturedCandidates.length} locator{capturedCandidates.length !== 1 ? 's' : ''} captured
            {capturedCandidates.length > 1 && ' · fallbacks applied in order'}
          </div>
        </div>
      )}

      {/* ── OR divider ── */}
      <div style={{ display:'flex', alignItems:'center', gap:10, margin:'12px 0' }}>
        <div style={{ flex:1, height:1, background:'#27272a' }} />
        <span style={{ fontSize:13, fontWeight:800, color:'#52525b', letterSpacing:'0.1em' }}>OR</span>
        <div style={{ flex:1, height:1, background:'#27272a' }} />
      </div>

      {/* ── Path 2: Enter Manually toggle ── */}
      <button onClick={handleOpenManual}
        style={{
          display:'flex', alignItems:'center', justifyContent:'center', gap:6,
          width:'100%', padding:'8px 12px', borderRadius:7, cursor:'pointer',
          fontSize:13, fontWeight:600,
          background: showManual ? 'rgba(124,58,237,.1)' : 'none',
          border: `1.5px ${showManual ? 'solid #7c3aed' : 'dashed #3f3f46'}`,
          color: showManual ? '#c4b5fd' : '#a1a1aa',
          transition:'all 0.15s',
        }}
        onMouseEnter={e => {
          if (!showManual) {
            (e.currentTarget).style.borderColor='#7c3aed';
            (e.currentTarget).style.color='#c4b5fd';
          }
        }}
        onMouseLeave={e => {
          if (!showManual) {
            (e.currentTarget).style.borderColor='#3f3f46';
            (e.currentTarget).style.color='#a1a1aa';
          }
        }}
      >
        <span style={{ fontSize:16 }}>{showManual ? '▾' : '+'}</span>
        {showManual ? 'Hide Manual Locators' : 'Enter Locators Manually'}
      </button>

      {/* ── Manual locator rows ── */}
      {showManual && (
        <div style={{
          marginTop:8, padding:'10px 12px', borderRadius:8,
          border:'1.5px solid #7c3aed', background:'#0d0d10',
          animation: manualPulsing
            ? 'plexFadeSlideIn 0.3s ease-out, plexPulsePurple 0.9s ease-in-out 0.3s 2'
            : 'plexFadeSlideIn 0.3s ease-out',
        }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#7c3aed', letterSpacing:'0.08em', marginBottom:8 }}>
            LOCATORS
            <span style={{ color:'#52525b', fontWeight:400 }}> — tried in order · first row = highest priority</span>
          </div>

          {manualRows.map((row, i) => (
            <div key={i} style={{ display:'flex', gap:6, marginBottom:6, alignItems:'center' }}>
              <span style={{ fontSize:11, color:'#52525b', minWidth:16, textAlign:'right' }}>{i + 1}</span>
              <select
                value={row.strategy}
                onChange={e => handleManualChange(i, { strategy: e.target.value as CandidateStrategy })}
                style={{
                  background:'#09090b', border:'1px solid #3f3f46', borderRadius:5,
                  padding:'5px 6px', color: STRATEGY_COLOR[row.strategy] ?? '#a1a1aa',
                  fontSize:11, outline:'none', cursor:'pointer', flexShrink:0,
                }}
              >
                {LOCATOR_STRATEGIES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <input
                value={row.value}
                onChange={e => handleManualChange(i, { value: e.target.value })}
                placeholder={placeholderFor(row.strategy)}
                style={{
                  flex:1, background:'#09090b', border:'1px solid #3f3f46', borderRadius:5,
                  padding:'5px 8px', color:'#e4e4e7', fontSize:11, outline:'none',
                  fontFamily: (row.strategy === 'css' || row.strategy === 'xpath') ? 'monospace' : 'inherit',
                }}
              />
              {manualRows.length > 1 && (
                <button onClick={() => removeManualRow(i)}
                  style={{ background:'none', border:'none', color:'#52525b', cursor:'pointer', fontSize:16, flexShrink:0, lineHeight:1 }}
                  onMouseEnter={e => { (e.currentTarget).style.color='#ef4444'; }}
                  onMouseLeave={e => { (e.currentTarget).style.color='#52525b'; }}
                >×</button>
              )}
            </div>
          ))}

          <button onClick={addManualRow}
            style={{
              marginTop:4, background:'none', border:'1px dashed #3f3f46',
              borderRadius:5, padding:'4px 10px', color:'#7c3aed', cursor:'pointer',
              fontSize:11, width:'100%',
            }}
            onMouseEnter={e => { (e.currentTarget).style.borderColor='#7c3aed'; }}
            onMouseLeave={e => { (e.currentTarget).style.borderColor='#3f3f46'; }}
          >+ Add fallback locator</button>

          <div style={{ marginTop:6, fontSize:10, color:'#3f3f46' }}>
            💡 Add multiple locators — Scriptwave tries each in order until one matches
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared label
// ─────────────────────────────────────────────────────────────────────────────

function FieldLabel({ field }: { field: CommandField }) {
  return (
    <div style={{ fontSize:11, color:'#71717a', marginBottom:5, display:'flex', alignItems:'center', gap:4 }}>
      <span>{field.label}</span>
      {field.required && <span style={{ color:'#ef4444', fontSize:12 }}>*</span>}
      {field.hint && <span style={{ color:'#3f3f46', fontSize:10 }}>· {field.hint}</span>}
    </div>
  );
}
